export type WorkspaceSubscription = {
  plan: 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'trialing' | 'sales_pending' | 'cancelled';
  employee_limit: number | null;
  trial_end?: string | null;
  workspace_mode: 'limited' | 'full';
};

export const LIMITED_HOME_PATH = '/chat';

const INDEPENDENT_MESSENGER_PATHS = new Set([
  '/chat',
  '/chat-audit',
  '/calls',
  '/calendar',
]);

const LIMITED_EXACT_PATHS = new Set([
  '/todo',
  '/todo/history',
  '/tasks',
  '/tasks/list',
  '/tasks/create',
  '/tasks/my-tasks',
  '/tasks/employee-tasks',
  '/tasks/recently-deleted',
  '/employee',
  '/employee/attendance',
  '/employee/leaves',
  '/attendance',
  '/leaves',
  '/employee/workflow/activity',
  '/employee/workflow/schedule',
  '/employee/workflow/comparison',
]);

export const isLimitedWorkspace = (
  subscription?: WorkspaceSubscription | null,
): boolean => subscription?.workspace_mode === 'limited';

export const isLimitedWorkspacePath = (pathname: string): boolean => {
  if (INDEPENDENT_MESSENGER_PATHS.has(pathname)) return true;
  if (pathname === '/leo') return true;
  if (LIMITED_EXACT_PATHS.has(pathname)) return true;
  const taskMatch = pathname.match(/^\/tasks\/([^/]+)(?:\/edit)?$/);
  if (!taskMatch) return false;
  return !new Set([
    'dashboard',
    'team-tasks',
    'reports',
    'code-settings',
  ]).has(taskMatch[1]);
};

export const isIndependentMessengerPath = (pathname: string): boolean =>
  INDEPENDENT_MESSENGER_PATHS.has(pathname);

// Starter routes share the normal Teamlio Access-profile keys. Normalize
// aliases and utility pages so direct URLs and navigation use the same panel
// decision as their parent feature.
export const accessPathForLimitedWorkspaceRoute = (
  pathname: string,
): string | null => {
  if (isIndependentMessengerPath(pathname)) return null;
  if (pathname === '/leo') return '/leo';
  if (pathname === '/employee' || pathname === '/attendance') {
    return '/employee/attendance';
  }
  if (pathname === '/leaves') return '/employee/leaves';
  if (pathname.startsWith('/employee/workflow/schedule')) {
    return '/employee/workflow/schedule';
  }
  if (pathname === '/employee/workflow/activity') {
    return '/employee/workflow/activity';
  }
  if (pathname === '/employee/workflow/comparison') {
    return '/employee/workflow/comparison';
  }
  if (pathname === '/todo' || pathname.startsWith('/todo/')) return '/todo';
  if (pathname === '/tasks' || pathname.startsWith('/tasks/')) return '/tasks/list';
  if (
    pathname === '/employee/attendance' ||
    pathname.startsWith('/employee/attendance/')
  ) {
    return '/employee/attendance';
  }
  if (
    pathname === '/employee/leaves' ||
    pathname.startsWith('/employee/leaves/')
  ) {
    return '/employee/leaves';
  }
  return pathname;
};

export const canAccessLio = (
  role: string | undefined,
  hasAccessProfile: boolean,
  isLioVisible: boolean,
): boolean => {
  const normalizedRole = (role || '').trim().toLowerCase();
  if (normalizedRole === 'admin' || normalizedRole === 'manager') return true;
  return hasAccessProfile && isLioVisible;
};
